"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getDefaultQueryRetry, WAVE_DROPS_PARAMS } from "@/components/react-query-wrapper/utils/query-utils";
import type { ApiDropsLeaderboardPage } from "@/generated/models/ApiDropsLeaderboardPage";
import { generateUniqueKeys, mapToExtendedDrops } from "@/helpers/waves/wave-drops.helpers";
import { commonApiFetch } from "@/services/api/common-api";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "react-use";
import useCapacitor from "./useCapacitor";
export enum WaveDropsLeaderboardSort {
  RANK = "RANK",
  RATING_PREDICTION = "RATING_PREDICTION",
  TREND = "TREND",
  MY_REALTIME_VOTE = "MY_REALTIME_VOTE",
  CREATED_AT = "CREATED_AT",
}
interface UseWaveDropsLeaderboardProps {
  readonly waveId: string;
  readonly sort?: WaveDropsLeaderboardSort | undefined;
  readonly pausePolling?: boolean | undefined;
  readonly curatedByGroupId?: string | undefined;
}
const SORT_DIRECTION_MAP: Record<WaveDropsLeaderboardSort, "ASC" | "DESC" | undefined> = {
  [WaveDropsLeaderboardSort.RANK]: undefined,
  [WaveDropsLeaderboardSort.RATING_PREDICTION]: "DESC",
  [WaveDropsLeaderboardSort.TREND]: "DESC",
  [WaveDropsLeaderboardSort.MY_REALTIME_VOTE]: undefined,
  [WaveDropsLeaderboardSort.CREATED_AT]: "DESC"
};
const POLLING_DELAY = 3000;
const ACTIVE_POLLING_INTERVAL = 5000;
const INACTIVE_POLLING_INTERVAL = 30000;
function useTabVisibility() {
  const $ = _c(2);
  const [isVisible, setIsVisible] = useState(!document.hidden);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const handleVisibilityChange = () => setIsVisible(!document.hidden);
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return isVisible;
}
export function useWaveDropsLeaderboard(t0) {
  const $ = _c(75);
  const {
    waveId,
    sort: t1,
    pausePolling: t2,
    curatedByGroupId
  } = t0;
  const sort = t1 === undefined ? WaveDropsLeaderboardSort.RANK : t1;
  const pausePolling = t2 === undefined ? false : t2;
  const {
    isCapacitor
  } = useCapacitor();
  const queryClient = useQueryClient();
  const [canPoll, setCanPoll] = useState(false);
  const isTabVisible = useTabVisibility();
  const pollingTimeoutRef = useRef(null);
  const sortDirection = SORT_DIRECTION_MAP[sort];
  let t3;
  if ($[0] !== curatedByGroupId) {
    t3 = curatedByGroupId?.trim() ?? undefined;
    $[0] = curatedByGroupId;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const normalizedCuratedByGroupId = t3;
  const t4 = normalizedCuratedByGroupId ?? null;
  let t5;
  if ($[2] !== sort || $[3] !== sortDirection || $[4] !== t4 || $[5] !== waveId) {
    t5 = [QueryKey.DROPS_LEADERBOARD, {
      waveId,
      page_size: WAVE_DROPS_PARAMS.limit,
      sort,
      sort_direction: sortDirection,
      curated_by_group: t4
    }];
    $[2] = sort;
    $[3] = sortDirection;
    $[4] = t4;
    $[5] = waveId;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const queryKey = t5 as const;
  let t6;
  if ($[7] !== normalizedCuratedByGroupId) {
    t6 = t7 => {
      const {
        pageParam,
        pageSize,
        targetSort,
        targetSortDirection
      } = t7;
      const params = {
        page_size: pageSize.toString(),
        sort: targetSort
      };
      if (targetSortDirection) {
        params.sort_direction = targetSortDirection;
      }
      if (typeof pageParam === "number") {
        params.page = `${pageParam}`;
      }
      if (normalizedCuratedByGroupId) {
        params.curated_by_group = normalizedCuratedByGroupId;
      }
      return params;
    };
    $[7] = normalizedCuratedByGroupId;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const buildLeaderboardParams = t6;
  let t7;
  if ($[9] !== buildLeaderboardParams || $[10] !== waveId) {
    t7 = async t8 => {
      const {
        pageParam: pageParam_0,
        pageSize: pageSize_0,
        targetSort: targetSort_0,
        targetSortDirection: targetSortDirection_0
      } = t8;
      return await commonApiFetch({
        endpoint: `waves/${waveId}/leaderboard`,
        params: buildLeaderboardParams({
          pageParam: pageParam_0,
          pageSize: pageSize_0,
          targetSort: targetSort_0,
          targetSortDirection: targetSortDirection_0
        })
      });
    };
    $[9] = buildLeaderboardParams;
    $[10] = waveId;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const fetchLeaderboardPage = t7;
  let t8;
  if ($[12] !== sort) {
    t8 = lastPage => {
      if (sort === WaveDropsLeaderboardSort.MY_REALTIME_VOTE) {
        const haveZeroVotes = lastPage.drops.some(_temp);
        if (haveZeroVotes) {
          return null;
        }
      }
      return lastPage.next ? lastPage.page + 1 : null;
    };
    $[12] = sort;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const getNextPageParam = t8;
  let t10;
  let t9;
  if ($[14] !== fetchLeaderboardPage || $[15] !== getNextPageParam || $[16] !== queryClient || $[17] !== queryKey || $[18] !== sort || $[19] !== sortDirection || $[20] !== waveId) {
    t9 = () => {
      if (!waveId) {
        return;
      }
      queryClient.prefetchInfiniteQuery({
        queryKey,
        queryFn: async t11 => {
          const {
            pageParam: pageParam_1
          } = t11;
          return await fetchLeaderboardPage({
            pageParam: pageParam_1,
            pageSize: WAVE_DROPS_PARAMS.limit,
            targetSort: sort,
            targetSortDirection: sortDirection
          });
        },
        initialPageParam: null,
        getNextPageParam,
        pages: 1,
        staleTime: 60000,
        ...getDefaultQueryRetry()
      });
    };
    t10 = [fetchLeaderboardPage, getNextPageParam, queryClient, queryKey, sort, sortDirection, waveId];
    $[14] = fetchLeaderboardPage;
    $[15] = getNextPageParam;
    $[16] = queryClient;
    $[17] = queryKey;
    $[18] = sort;
    $[19] = sortDirection;
    $[20] = waveId;
    $[21] = t10;
    $[22] = t9;
  } else {
    t10 = $[21];
    t9 = $[22];
  }
  useEffect(t9, t10);
  let t11;
  if ($[23] !== fetchLeaderboardPage || $[24] !== sort || $[25] !== sortDirection) {
    t11 = async t12 => {
      const {
        pageParam: pageParam_2
      } = t12;
      return await fetchLeaderboardPage({
        pageParam: pageParam_2,
        pageSize: WAVE_DROPS_PARAMS.limit,
        targetSort: sort,
        targetSortDirection: sortDirection
      });
    };
    $[23] = fetchLeaderboardPage;
    $[24] = sort;
    $[25] = sortDirection;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const t12 = !pausePolling && !!waveId;
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = getDefaultQueryRetry();
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== getNextPageParam || $[29] !== queryKey || $[30] !== t11 || $[31] !== t12) {
    t14 = {
      queryKey,
      queryFn: t11,
      initialPageParam: null,
      getNextPageParam,
      enabled: t12,
      staleTime: 60000,
      ...t13
    };
    $[28] = getNextPageParam;
    $[29] = queryKey;
    $[30] = t11;
    $[31] = t12;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(t14);
  let t15;
  if ($[33] !== data || $[34] !== sort) {
    bb0: {
      if (!data?.pages) {
        let t16;
        if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
          t16 = [];
          $[36] = t16;
        } else {
          t16 = $[36];
        }
        t15 = t16;
        break bb0;
      }
      const mappedDrops = mapToExtendedDrops(data.pages.map(_temp2), []);
      const uniqueDrops = generateUniqueKeys(mappedDrops, []);
      if (sort === WaveDropsLeaderboardSort.MY_REALTIME_VOTE) {
        t15 = uniqueDrops.filter(_temp3);
        break bb0;
      }
      t15 = uniqueDrops;
    }
    $[33] = data;
    $[34] = sort;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const drops = t15;
  const hasInitialized = !!data?.pages;
  let t16;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setCanPoll(true);
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== data) {
    t17 = [data];
    $[38] = data;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  useDebounce(t16, 10000, t17);
  let t18;
  if ($[40] !== drops) {
    t18 = drops.some(_temp4);
    $[40] = drops;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const hasTempDrop = t18;
  const canAutoRefetch = isTabVisible && !hasTempDrop;
  let t19;
  if ($[42] !== drops) {
    t19 = pollingData => {
      if (pollingData.drops.length === 0 || drops.length === 0) {
        return false;
      }
      const latestPolledDrop = pollingData.drops[0];
      const newestExistingTimestamp = Math.max(...drops.map(_temp5));
      if (latestPolledDrop) {
        const polledCreatedAt = new Date(latestPolledDrop.created_at).getTime();
        return polledCreatedAt > newestExistingTimestamp;
      }
      return true;
    };
    $[42] = drops;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  const checkForNewDrops = t19;
  let t20;
  if ($[44] !== queryKey) {
    t20 = [...queryKey, "polling"];
    $[44] = queryKey;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== canAutoRefetch || $[47] !== checkForNewDrops || $[48] !== fetchLeaderboardPage || $[49] !== refetch) {
    t21 = async () => {
      const result = await fetchLeaderboardPage({
        pageParam: null,
        pageSize: 1,
        targetSort: WaveDropsLeaderboardSort.CREATED_AT,
        targetSortDirection: "DESC"
      });
      if (canAutoRefetch && checkForNewDrops(result)) {
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
        }
        pollingTimeoutRef.current = setTimeout(() => {
          refetch();
        }, POLLING_DELAY);
      }
      return result;
    };
    $[46] = canAutoRefetch;
    $[47] = checkForNewDrops;
    $[48] = fetchLeaderboardPage;
    $[49] = refetch;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  const t22 = canPoll && !pausePolling;
  const t23 = isTabVisible ? ACTIVE_POLLING_INTERVAL : INACTIVE_POLLING_INTERVAL;
  const t24 = !isCapacitor;
  let t25;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = getDefaultQueryRetry();
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== checkForNewDrops || $[53] !== t20 || $[54] !== t21 || $[55] !== t22 || $[56] !== t23 || $[57] !== t24) {
    t26 = {
      queryKey: t20,
      queryFn: t21,
      select: checkForNewDrops,
      enabled: t22,
      refetchInterval: t23,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchIntervalInBackground: t24,
      ...t25
    };
    $[52] = checkForNewDrops;
    $[53] = t20;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
    $[57] = t24;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  const {
    data: t27
  } = useQuery(t26);
  const haveNewDrops = t27 === undefined ? false : t27;
  let t28;
  let t29;
  if ($[59] !== queryClient || $[60] !== waveId) {
    t28 = () => () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
      queryClient.removeQueries({
        queryKey: [QueryKey.DROPS, {
          waveId
        }]
      });
    };
    t29 = [waveId, queryClient];
    $[59] = queryClient;
    $[60] = waveId;
    $[61] = t28;
    $[62] = t29;
  } else {
    t28 = $[61];
    t29 = $[62];
  }
  useEffect(t28, t29);
  let t30;
  if ($[63] !== fetchNextPage || $[64] !== hasNextPage) {
    t30 = async () => {
      if (hasNextPage) {
        await fetchNextPage();
      }
    };
    $[63] = fetchNextPage;
    $[64] = hasNextPage;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  const manualFetch = t30;
  const t31 = isFetching || !hasInitialized;
  let t32;
  if ($[66] !== drops || $[67] !== fetchNextPage || $[68] !== hasNextPage || $[69] !== haveNewDrops || $[70] !== isFetchingNextPage || $[71] !== manualFetch || $[72] !== refetch || $[73] !== t31) {
    t32 = {
      drops,
      fetchNextPage,
      hasNextPage,
      isFetching: t31,
      isFetchingNextPage,
      refetch,
      haveNewDrops,
      manualFetch
    };
    $[66] = drops;
    $[67] = fetchNextPage;
    $[68] = hasNextPage;
    $[69] = haveNewDrops;
    $[70] = isFetchingNextPage;
    $[71] = manualFetch;
    $[72] = refetch;
    $[73] = t31;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  return t32;
}
function _temp5(drop_2) {
  return new Date(drop_2.created_at).getTime();
}
function _temp4(drop_1) {
  return drop_1.id.startsWith("temp-");
}
function _temp3(drop_0) {
  return drop_0.context_profile_context?.rating !== 0;
}
function _temp2(page) {
  return {
    wave: page.wave,
    drops: page.drops
  };
}
function _temp(drop) {
  return drop.context_profile_context?.rating === 0;
}